import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "utils/cn";
import { Spinner } from "./Spinner";

export const buttonVariants = cva(
  [
    "inline-flex select-none items-center justify-center gap-2 whitespace-nowrap font-medium",
    "transition-colors duration-fast ease-standard",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground shadow-xs hover:bg-primary-hover",
        secondary:
          "border border-input bg-surface text-foreground shadow-xs hover:bg-surface-muted",
        outline: "border border-primary/40 text-primary-text hover:bg-primary-subtle",
        ghost: "text-foreground hover:bg-surface-muted",
        "primary-ghost": "text-primary-text hover:bg-primary-subtle",
        danger: "bg-danger text-white shadow-xs hover:bg-danger/90",
        "danger-outline":
          "border border-danger/30 bg-surface text-danger-text shadow-xs hover:bg-danger-subtle",
        "danger-ghost": "text-danger-text hover:bg-danger-subtle",
        link: "text-primary-text underline-offset-4 hover:underline",
      },
      size: {
        xs: "h-7 rounded-control px-2.5 text-xs [&_svg]:h-3.5 [&_svg]:w-3.5",
        sm: "h-8 rounded-control px-3 text-xs [&_svg]:h-3.5 [&_svg]:w-3.5",
        md: "h-9 rounded-control px-3.5 text-sm [&_svg]:h-4 [&_svg]:w-4",
        lg: "h-11 rounded-control px-5 text-sm [&_svg]:h-4 [&_svg]:w-4",
        icon: "h-9 w-9 rounded-control [&_svg]:h-4 [&_svg]:w-4",
        "icon-sm": "h-8 w-8 rounded-control [&_svg]:h-4 [&_svg]:w-4",
      },
      fullWidth: {
        true: "w-full",
      },
    },
    compoundVariants: [{ variant: "link", class: "h-auto px-0" }],
    defaultVariants: { variant: "primary", size: "md" },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render the child element (e.g. a router Link) with button styling instead of a <button>. */
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      asChild = false,
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      type = "button",
      children,
      ...props
    },
    ref
  ) => {
    const classes = cn(buttonVariants({ variant, size, fullWidth }), className);
    if (asChild) {
      return (
        <Slot ref={ref} data-ui="button" className={classes} {...props}>
          {children}
        </Slot>
      );
    }
    return (
      <button
        ref={ref}
        type={type}
        data-ui="button"
        className={classes}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? <Spinner /> : leftIcon}
        {children}
        {!loading && rightIcon}
      </button>
    );
  }
);
Button.displayName = "Button";
